<script setup>
import { computed, ref, watch } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import * as Sentry from "@sentry/vue";
import { chunk, cloneDeep, isEmpty, isObjectLike } from "lodash";
import Draggable from "vuedraggable";
import { useModalogStore } from "@/stores/modalog.js";
import { useModalogEscape } from "@/composables/useModalogEscape.js";
import BrandThrobber from "@/Components/Brand/BrandThrobber.vue";
import { useToastStore } from "@/stores/toast.js";

const modalogStore = useModalogStore();
const toastStore = useToastStore();

useModalogEscape();

const props = defineProps({
    itemsPerPage: {
        type: Number,
        default: 15,
        required: false,
    },
    listUuid: {
        type: String,
        required: true,
    },
});

// Gifts render as one draggable grid per wish-list page, separated by dashed
// dividers, so the modal mirrors where the list's real page breaks fall.
// The grids share a drag group, letting gifts move across page boundaries.
const pages = ref([]);
const reorderedItems = ref({});
const saving = ref(false);

const isSaveDisabled = computed(
    () => isEmpty(reorderedItems.value) || saving.value,
);

let leaving = false;

function currentItems() {
    const items = usePage().props.items;

    return items?.data || items || [];
}

// The layout keeps this component mounted after it closes (the store only
// flips hasModalog), so every opening must start over from the list's
// current gifts with a clean save state. Otherwise a second reorder in one
// session shows the stale order, inherits the "Saving" flag from the last
// save, and the leaving guard swallows the trip back to the list.
function reset() {
    pages.value = chunk(cloneDeep(currentItems()), props.itemsPerPage);
    reorderedItems.value = {};
    saving.value = false;
    leaving = false;
}

reset();

// The modalog rides the list.reorder route, so every close path (X, backdrop,
// ESC via the shared composable) must navigate back to the list instead of
// stranding a hidden modal on the reorder URL.
function goBackToList() {
    if (leaving) {
        return;
    }

    leaving = true;
    modalogStore.closeModalog();
    router.get(
        route("list.show", { giftlist: props.listUuid }),
        {},
        {
            preserveState: false,
            replace: true,
        },
    );
}

watch(
    () => modalogStore.hasModalog,
    (isOpen) => {
        if (isOpen) {
            reset();
        } else if (route().current() === "list.reorder") {
            goBackToList();
        }
    },
);

function calculateNewSequence(flat, globalIndex, item) {
    const itemLeft = flat[globalIndex - 1];
    const itemLeftSequence = isObjectLike(itemLeft) ? itemLeft.sequence : null;
    const itemRight = flat[globalIndex + 1];
    const itemRightSequence = isObjectLike(itemRight)
        ? itemRight.sequence
        : null;

    if (itemLeftSequence && itemRightSequence) {
        // Item moved between two items
        return (itemLeftSequence + itemRightSequence) / 2;
    } else if (itemLeftSequence) {
        // Item moved to end
        return itemLeftSequence - 1000;
    } else if (itemRightSequence) {
        // Item moved to beginning
        return itemRightSequence + 1000;
    }

    return item.sequence;
}

function handlePageChange(event) {
    // A cross-page drag fires `removed` on the source grid and `added` on the
    // target; the sequence only needs computing once, where the item landed.
    const change = event.moved ?? event.added;

    if (!change) {
        return;
    }

    // v-model has already applied the drop, so the moved gift's flat position
    // gives its new neighbors.
    const flat = pages.value.flat();
    const globalIndex = flat.findIndex(
        (candidate) => candidate.uuid === change.element.uuid,
    );
    const item = flat[globalIndex];
    const newSequence = calculateNewSequence(flat, globalIndex, item);

    reorderedItems.value[item.uuid] = newSequence;
    item.sequence = newSequence;

    // Rebalance to the real page size so the separators stay truthful after
    // a cross-page drag leaves the source page short.
    pages.value = chunk(flat, props.itemsPerPage);
}

function saveOrder() {
    saving.value = true;

    axios
        .put(route("item.reorder"), { items: reorderedItems.value })
        .then(() => {
            toastStore.success("Your gift order was saved.");
            goBackToList();
        })
        .catch((error) => {
            // Clear the busy flag first so the button recovers even if
            // reporting throws.
            saving.value = false;
            toastStore.error(
                "We couldn't save your gift order. Please try again.",
            );
            console.error(error);
            Sentry.captureException(error);
        });
}
</script>

<template>
    <!-- Stable root + hasModalog gate, per the brand-modalog pattern. -->
    <div>
        <div
            v-if="modalogStore.hasModalog"
            class="brand fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-navy/60 p-4 font-brand text-brand-navy sm:p-8"
            @click.self="goBackToList"
        >
            <div
                class="relative my-auto flex max-h-[85vh] w-full max-w-[762px] flex-col overflow-hidden rounded-[5px] bg-white shadow-brand-hero"
            >
                <header
                    class="flex shrink-0 items-center justify-between px-9 py-6"
                    :style="{
                        backgroundImage:
                            'linear-gradient(99deg, #ff9d3c -10%, #f21439 85%)',
                    }"
                >
                    <h2 class="text-[25px] font-bold text-white">
                        Reorder Your Gifts
                    </h2>
                    <button
                        type="button"
                        class="grid size-8 place-items-center text-white transition hover:opacity-75"
                        aria-label="Close"
                        @click="goBackToList"
                    >
                        <svg
                            viewBox="0 0 19 19"
                            class="size-[19px]"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            stroke-linecap="round"
                            aria-hidden="true"
                        >
                            <path d="M2 2l15 15M17 2L2 17" />
                        </svg>
                    </button>
                </header>

                <div class="shrink-0 px-9 pt-6">
                    <p
                        class="text-[15px] font-bold leading-relaxed text-[#3c3938]"
                    >
                        Reorganizing your gifts is simple!
                    </p>
                    <p class="text-[15px] leading-relaxed text-[#3c3938]">
                        Just drag and drop into the order you&rsquo;d like, hit
                        save, and we&rsquo;ll do the rest :)
                    </p>
                </div>

                <div class="min-h-0 flex-1 overflow-y-auto px-9 py-6">
                    <template
                        v-for="(page, pageIndex) in pages"
                        :key="pageIndex"
                    >
                        <!-- Dashed separators mark the list's page breaks. -->
                        <div
                            v-if="pageIndex > 0"
                            class="my-6 border-t border-dashed border-[#cdc4c2]"
                            aria-hidden="true"
                        ></div>
                        <Draggable
                            v-model="pages[pageIndex]"
                            group="reorganize-gifts"
                            item-key="uuid"
                            class="grid grid-cols-2 gap-4 sm:grid-cols-4"
                            :animation="150"
                            @change="handlePageChange"
                        >
                            <template #item="{ element }">
                                <div
                                    class="cursor-grab select-none rounded-[5px] border border-solid border-[#e6dfdd] bg-white p-2 active:cursor-grabbing"
                                >
                                    <div
                                        class="aspect-[146/109] w-full overflow-hidden rounded-[2px] bg-[#f4f4f6]"
                                    >
                                        <img
                                            v-if="
                                                element.image_url?.img_2x ||
                                                element.image_url?.img_1x
                                            "
                                            :src="
                                                element.image_url?.img_2x ??
                                                element.image_url?.img_1x
                                            "
                                            :alt="element.name"
                                            class="pointer-events-none size-full object-cover"
                                        />
                                    </div>
                                    <p
                                        class="mt-2 line-clamp-2 min-h-[31px] px-1 pb-1 text-[12px] font-semibold leading-[1.3] text-[#191e3e]"
                                    >
                                        {{ element.name }}
                                    </p>
                                </div>
                            </template>
                        </Draggable>
                    </template>
                </div>

                <footer
                    class="flex shrink-0 items-center justify-between border-t border-solid border-black/10 px-9 py-5"
                >
                    <button
                        type="button"
                        class="text-[15px] font-bold text-[#191e3e] underline underline-offset-2 transition hover:text-brand-red"
                        @click="goBackToList"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="inline-flex items-center gap-2 rounded-brand bg-brand-navy px-8 py-3.5 text-[15px] font-bold text-white transition hover:bg-brand-navy/90"
                        :class="saving ? '' : 'disabled:opacity-60'"
                        :disabled="isSaveDisabled"
                        @click="saveOrder"
                    >
                        {{ saving ? "Saving" : "Save Order" }}
                        <BrandThrobber v-if="saving" />
                    </button>
                </footer>
            </div>
        </div>
    </div>
</template>
